import { Link } from "react-router-dom";
import { useState } from "react";
import { Eye, Plus, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { selectClass } from "../components/FormField";
import {
  PROCESSED_DISPUTE_DEFAULT_STATUS,
  processedDisputeStatusFilterOptions,
} from "../data";
import { useDisputes } from "../hooks/useDisputes";
import type { DisputeStatus } from "../types";
import { cn } from "@/shared/lib/cn";

type ProcessedDisputeStatus = Extract<DisputeStatus, "closed" | "resolved" | "rejected">;

const statusBadgeClass: Record<DisputeStatus, string> = {
  inProgress: "bg-accent-soft text-accent-foreground",
  resolved: "bg-accent/15 text-accent",
  rejected: "bg-danger-soft text-danger",
  closed: "bg-card-muted text-muted-foreground",
};

export function ProcessedDisputesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.disputes.list;

  const [draftStatus, setDraftStatus] = useState<ProcessedDisputeStatus>(
    PROCESSED_DISPUTE_DEFAULT_STATUS,
  );
  const [appliedStatus, setAppliedStatus] = useState<ProcessedDisputeStatus>(
    PROCESSED_DISPUTE_DEFAULT_STATUS,
  );

  const { disputes: filtered, loading } = useDisputes({ status: appliedStatus });

  const applyFilters = () => {
    setAppliedStatus(draftStatus);
  };

  const resetFilters = () => {
    setDraftStatus(PROCESSED_DISPUTE_DEFAULT_STATUS);
    setAppliedStatus(PROCESSED_DISPUTE_DEFAULT_STATUS);
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Breadcrumbs homeHref="/dashboard/hospital" items={[{ label: labels.pageTitle }]} />
        <Link
          to="/dashboard/disputes/create"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.createButton}
        </Link>
      </div>

      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <select
            value={draftStatus}
            onChange={(event) =>
              setDraftStatus(event.target.value as ProcessedDisputeStatus)
            }
            className={`${selectClass} w-full min-w-[200px] max-w-xs`}
          >
            {processedDisputeStatusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {labels.statuses[option.value]}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={applyFilters}
              className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
            >
              {t.safecheck.filter}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center px-5 py-16">
            <p className="text-sm text-muted">Chargement…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center px-5 py-16">
            <p className="text-sm text-muted">{labels.empty}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="safecheck-table w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-border bg-card-muted/50">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.columns.id}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.columns.date}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.columns.company}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.columns.status}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {labels.columns.actions}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((dispute, index) => (
                  <tr
                    key={dispute.id}
                    className={cn(
                      "border-b border-border last:border-0 hover:bg-card-muted/30",
                      index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                    )}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {dispute.reference}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {dispute.submittedDate || dispute.createdAt}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{dispute.company}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                          statusBadgeClass[dispute.status],
                        )}
                      >
                        {labels.statuses[dispute.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/disputes/${dispute.id}`}
                        aria-label={labels.actions.view}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:border-primary hover:bg-primary-soft"
                      >
                        <Eye size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
